import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Vendor } from '../lib/types';
import { useApiQuery } from '../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../ui/components';

export function Suppliers() {
  const { persona } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    return `/api/v1/commitments/vendors?${p}`;
  }, [q, status]);
  const vendorsQ = useApiQuery<{ items: Vendor[] }>(query);

  const [showRegister, setShowRegister] = useState(false);
  const [statusTarget, setStatusTarget] = useState<Vendor | null>(null);

  const canRegister = persona?.roles.includes('MDA_PROCUREMENT_OFFICER') ?? false;
  const canChangeStatus = persona?.roles.includes('MDA_HEAD_FINANCE') ?? false;

  return (
    <div>
      <PageHeader
        title="Government supplier registry"
        subtitle="Businesses registered to receive payments from the Consolidated Fund"
        actions={
          canRegister ? (
            <button className="btn-primary" onClick={() => setShowRegister(true)}>
              + Register supplier
            </button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="field-input max-w-xs"
          placeholder="Search by name or TIN…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="field-input max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="BLACKLISTED">Blacklisted</option>
        </select>
      </div>

      {vendorsQ.error ? <ErrorNote message={vendorsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {vendorsQ.loading ? (
          <Spinner />
        ) : (vendorsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No suppliers found" hint="Register the businesses that work with government." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Supplier</th>
                <th className="table-head">TIN</th>
                <th className="table-head">Bank account</th>
                <th className="table-head">SPF employer</th>
                <th className="table-head">Status</th>
                <th className="table-head">Registered</th>
                {canChangeStatus ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {vendorsQ.data!.items.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{v.name}</td>
                  <td className="table-cell tabular-nums">{v.tin}</td>
                  <td className="table-cell tabular-nums">{v.bankAccount}</td>
                  <td className="table-cell">{v.spfEmployerNo ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                  <td className="table-cell text-slate-500">{formatDate(v.createdAt)}</td>
                  {canChangeStatus ? (
                    <td className="table-cell text-right">
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setStatusTarget(v)}>
                        Change status
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onDone={() => {
            setShowRegister(false);
            vendorsQ.reload();
          }}
        />
      ) : null}
      {statusTarget ? (
        <StatusModal
          vendor={statusTarget}
          onClose={() => setStatusTarget(null)}
          onDone={() => {
            setStatusTarget(null);
            vendorsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ name: '', tin: '', spfEmployerNo: '', bankAccount: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/commitments/vendors', {
        name: form.name,
        tin: form.tin,
        bankAccount: form.bankAccount,
        ...(form.spfEmployerNo ? { spfEmployerNo: form.spfEmployerNo } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register supplier');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register government supplier" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Business name">
          <input
            className="field-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Constance Ephelia Resort"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="TIN (9 digits, SRC)">
            <input
              className="field-input"
              value={form.tin}
              onChange={(e) => setForm({ ...form, tin: e.target.value })}
              placeholder="5XXXXXXXX"
            />
          </Field>
          <Field label="SPF employer no. (optional)">
            <input
              className="field-input"
              value={form.spfEmployerNo}
              onChange={(e) => setForm({ ...form, spfEmployerNo: e.target.value })}
              placeholder="EMP-0000"
            />
          </Field>
        </div>
        <Field label="Bank account for payment">
          <input
            className="field-input"
            value={form.bankAccount}
            onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
            placeholder="Account number"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.name || !form.tin || !form.bankAccount}>
            {busy ? 'Registering…' : 'Register supplier'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StatusModal({ vendor, onClose, onDone }: { vendor: Vendor; onClose: () => void; onDone: () => void }) {
  const [status, setStatus] = useState(vendor.status);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/commitments/vendors/${vendor.id}/status`, { status, reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Supplier status — ${vendor.name}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="New status">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as Vendor['status'])}>
            <option value="ACTIVE">ACTIVE — eligible for payment</option>
            <option value="SUSPENDED">SUSPENDED — temporarily barred</option>
            <option value="BLACKLISTED">BLACKLISTED — removed from register</option>
          </select>
        </Field>
        <Field label="Reason (recorded on the audit chain)">
          <textarea
            className="field-input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Tax compliance certificate expired"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Saving…' : 'Update status'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
